import type { Cell, Column, Cue, Rundown } from '@rundown-studio/types'
import type { IndexedCue } from '../cues-list/index.ts'
import type { Dictionary } from 'lodash'

import {
  groupBy as _groupBy,
  keyBy as _keyBy,
  mapValues as _mapValues,
  uniq as _uniq,
} from 'lodash'

import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'

import {
  rundownAtom,
  indexedCuesWithoutSkippedAtom,
  cuesAtom,
  cellsAtom,
  columnsAtom,
} from '../../store/rundown.store.ts'
import { momentAtom } from '@/store/moment.store.ts'
import { timestampsAtom } from '@/store/timestamps.store.ts'
import { useRundownOutputOptions } from './useRundownOutputOptions.ts'

import {
  CUE_TYPE_CUE,
  CUE_TYPE_GROUP,
  CUE_TYPE_HEADING,
} from '../../constants/cue-types.ts'
import CueItemElapsed from '@/components/rundown/CueItemElapsed.jsx'

import * as OUTPUT_TYPES from './rundown-output.const.ts'
import { CELL_SELECT_COLORS } from '@rundown-studio/consts'
import { useSearchParams } from 'react-router-dom'
import type { CSSProperties, PropsWithChildren } from 'react'
import { useMemo, lazy, Suspense } from 'react'
import _indexOf from 'lodash/indexOf'

import { CueRunState } from '@rundown-studio/utils'
import { addMilliseconds } from 'date-fns'
import formatTimeOfDay from '../../utils/formatTimeOfDay.js'
import { formatDurationHuman } from '../../utils/index.ts'
import { extractTextNewLines } from '../../utils/index.js'
import {
  CuesGroupPlannedTimeRange,
  useGroupTiming,
  useGroupTimestamps,
} from '../../entities/cues-group/index.ts'

import { CueProvider } from '../cue/index.js'

import {
  getContentForSelectCell,
  getContentForTextCell,
} from '../rundown/cell/content/content.helpers.ts'

import { CellTypeSelectStatic } from '../rundown/cell/CellTypeSelect.tsx'
import { ContentPreviewRenderer } from '../rundown/cell/content/ContentPreviewRenderer.tsx'
import { cn } from 'tailwind-variants'

const ScrollIntoView = lazy(
  () => import('@/components/rundown/ScrollIntoView.jsx'),
)

// Minimum width (rem) for the left block (index / times / title) so it never
// crushes on narrow screens. Sized to hold the index, the 120px time column and
// a readable slice of the title. Above this it stays a fluid ~1/3 of the row,
// matching the original wide-screen layout (see buildGridStyle).
const LEFT_COL_MIN_REM = 22
// Minimum readable width (rem) for each cell column. When the visible columns
// can't all fit at this width the body scrolls horizontally rather than
// crushing the text to one character per line on small screens.
const CELL_COL_MIN_REM = 9
// Matches the `gap-4` (1rem) gap between grid tracks.
const GRID_GAP_REM = 1

/**
 * Build the grid style shared by the header and every row so the cell columns
 * stay aligned while the whole body scrolls horizontally as one unit.
 *
 * `gridTemplateColumns` reproduces the original proportions: the left block
 * takes ~1/3 of the row and the cell columns share the remaining ~2/3 evenly
 * (left track of `columnCount / 2` fr against `1fr` cells keeps that 1:2 ratio
 * for any column count).
 *
 * `minWidth` is the explicit sum of every track's minimum plus the gaps, so on
 * narrow screens the row grows to keep each track at its readable minimum and
 * the body scrolls horizontally — instead of the `1fr` tracks collapsing below
 * their `minmax` floor (which happens when overflow-hidden cell content reports
 * no min-content width). On wide screens minWidth is below the viewport, so the
 * `fr` tracks expand to fill and no scrollbar appears.
 */
function buildGridStyle(columnCount: number): CSSProperties {
  if (columnCount === 0) {
    return {
      gridTemplateColumns: `minmax(${LEFT_COL_MIN_REM}rem, 1fr)`,
      minWidth: `${LEFT_COL_MIN_REM}rem`,
    }
  }
  const leftFr = columnCount / 2
  const minWidthRem =
    LEFT_COL_MIN_REM +
    columnCount * CELL_COL_MIN_REM +
    columnCount * GRID_GAP_REM
  return {
    gridTemplateColumns: `minmax(${LEFT_COL_MIN_REM}rem, ${leftFr}fr) repeat(${columnCount}, minmax(${CELL_COL_MIN_REM}rem, 1fr))`,
    minWidth: `${minWidthRem}rem`,
  }
}

export function RundownOutputLayoutList() {
  const rundown = useAtomValue(rundownAtom)
  const cues = useAtomValue(cuesAtom)
  const columns = useAtomValue(columnsAtom)
  const cells = useAtomValue(cellsAtom)

  const [searchParams] = useSearchParams()

  const visibleColumnsSearchParam = searchParams.get(OUTPUT_TYPES.COLUMNS) || ''

  const visibleColumnIds = useMemo(
    () => _uniq(visibleColumnsSearchParam.split(',').filter(Boolean)),
    [visibleColumnsSearchParam],
  )
  const visibleColumns = useMemo(
    () => columns.filter((column) => visibleColumnIds.includes(column.id)),
    [visibleColumnIds, columns],
  )

  // Filter out any cells that are part of non-visible columns
  // and group then within objects Record<CueId, Record<ColumnId, Cell[]>>
  const filteredCells = useMemo(() => {
    const filtered = cells.filter((cell) =>
      visibleColumnIds.includes(cell.columnId),
    )
    return _mapValues(_groupBy(filtered, 'cueId'), (group) =>
      _keyBy(group, 'columnId'),
    )
  }, [visibleColumnIds, cells])

  const indexedCues = useAtomValue(indexedCuesWithoutSkippedAtom)

  if (!rundown) return null

  return (
    <div className="p-2 flex flex-col gap-4 mb-[90vh]">
      {/* Column titles */}
      <div
        className="sticky top-0 grid gap-4 z-50 bg-black"
        style={buildGridStyle(visibleColumns.length)}
      >
        {/* Spacer aligning with the left block of each row */}
        <div></div>
        {visibleColumns.map(({ name }) => (
          <div key={name} className="border-b-2 border-white/20">
            {name}
          </div>
        ))}
      </div>

      {indexedCues.map((indexedCue) => {
        const cue = cues[indexedCue.id]
        if (!cue) return null

        // Skip group children — they're rendered inside CueItemGroup
        if (indexedCue.parentId) return null

        if (cue.type === CUE_TYPE_HEADING) {
          return <CueItemHeading key={indexedCue.id} cue={cue} />
        }

        if (cue.type === CUE_TYPE_GROUP) {
          return (
            <CueProvider key={indexedCue.id} cueId={cue.id}>
              <CueItemGroup
                index={indexedCue.displayIndex}
                cue={cue}
                groupChildren={indexedCue.children}
                cues={cues}
                timezone={rundown?.timezone}
                cells={filteredCells}
                visibleColumns={visibleColumns}
                todDisplayFormat={rundown.settings?.todDisplayFormat}
              />
            </CueProvider>
          )
        }

        return (
          <CueProvider key={indexedCue.id} cueId={cue.id}>
            <CueItemCue
              index={indexedCue.displayIndex}
              timestampId={indexedCue.id}
              cue={cue}
              timezone={rundown?.timezone}
              cells={filteredCells[indexedCue.id]}
              visibleColumns={visibleColumns}
              todDisplayFormat={rundown.settings?.todDisplayFormat}
            />
          </CueProvider>
        )
      })}
    </div>
  )
}

type CueItemGroupProps = {
  index: string | number
  cue: Cue
  groupChildren: IndexedCue['children']
  cues: Record<string, Cue>
  timezone: Rundown['timezone']
  cells: object
  visibleColumns: Column[]
  todDisplayFormat: Rundown['settings']['todDisplayFormat']
}

function CueItemGroup({
  index,
  cue,
  groupChildren,
  cues,
  timezone,
  cells,
  visibleColumns,
  todDisplayFormat,
}: CueItemGroupProps) {
  const { getOption } = useRundownOutputOptions()

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) === OUTPUT_TYPES.ON
      ? cue.backgroundColor
      : 'transparent'

  let displayChildIndex = 0

  return (
    <div className="w-full space-y-4">
      <div
        className="min-h-20 p-3 bg-gray-950 rounded-sm flex items-center gap-8 text-2xl"
        style={{
          backgroundColor: cueBackgroundColor || 'transparent',
        }}
      >
        <CueIndex>{index}</CueIndex>
        <div className="flex min-w-0 grow flex-col gap-1 wrap-break-word">
          {/* Cue title */}
          <p>{cue.title}</p>

          {/* Cue timing */}
          <CueItemGroupTiming
            timezone={timezone}
            groupChildren={groupChildren}
          />
        </div>
      </div>
      {Object.values(groupChildren ?? {}).map((_cue) => {
        const cue = cues[_cue.id]

        if (!cue) return null

        if (cue.type === CUE_TYPE_HEADING)
          return <CueItemHeading key={index} cue={cue} />

        // Skip individually skipped children within a visible group
        if (cue.settings?.skipDuringShow) return null

        displayChildIndex++

        return (
          <CueItemCue
            key={`${index}_child_${displayChildIndex}`}
            index={`${index}.${displayChildIndex}`}
            timestampId={_cue.id}
            cue={cue}
            timezone={timezone}
            cells={cells[cue.id]}
            visibleColumns={visibleColumns}
            todDisplayFormat={todDisplayFormat}
          />
        )
      })}
    </div>
  )
}

function CueItemGroupTiming({
  timezone,
  groupChildren,
}: Pick<CueItemGroupProps, 'timezone' | 'groupChildren'> & {}) {
  const groupTimestamps = useGroupTimestamps(groupChildren || [])
  const groupTiming = useGroupTiming(groupTimestamps)

  return (
    <CuesGroupPlannedTimeRange
      groupTiming={groupTiming}
      className="text-sm! font-mono"
      timezone={timezone}
    />
  )
}

function CueItemHeading({ cue }: { cue: Cue }) {
  const { getOption } = useRundownOutputOptions()

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) === OUTPUT_TYPES.ON
      ? cue.backgroundColor
      : 'transparent'

  return (
    <div
      className="w-full px-3 py-1 rounded-sm flex items-center gap-8 text-2xl"
      style={{
        backgroundColor: cueBackgroundColor || 'transparent',
      }}
    >
      <div className="min-w-0 grow wrap-break-word">
        <p>{cue.title}</p>
      </div>
    </div>
  )
}

function CueIndex({ children }: PropsWithChildren) {
  return <p className="shrink-0 whitespace-nowrap min-w-[2ch]">{children}</p>
}

type CueItemCueProps = {
  index: string | number
  timestampId: string
  cue: Cue
  timezone: Rundown['timezone']
  cells?: Dictionary<Cell>
  visibleColumns: Column[]
  todDisplayFormat: Rundown['settings']['todDisplayFormat']
}

function CueItemCue({
  index,
  timestampId,
  cue,
  timezone,
  cells = {},
  visibleColumns,
  todDisplayFormat,
}: CueItemCueProps) {
  /** Cached atom access that only re-renders when specific state changes  */
  const timestampStateAtom = useMemo(
    () =>
      selectAtom(timestampsAtom, (atoms) => atoms?.cues?.[timestampId]?.state),
    [timestampId],
  )

  const timestampState = useAtomValue(timestampStateAtom)

  const { getOption } = useRundownOutputOptions()

  const cueBackgroundColor =
    getOption(OUTPUT_TYPES.CUE_BACKGROUND_COLORS) === OUTPUT_TYPES.ON
      ? cue.backgroundColor
      : 'transparent'

  const isTypeCue = useMemo(() => cue.type === CUE_TYPE_CUE, [cue.type])
  const isActive = useMemo(
    () => isTypeCue && timestampState === CueRunState.CUE_ACTIVE,
    [isTypeCue, timestampState],
  )
  const cueStyle = useMemo(
    () =>
      isTypeCue
        ? {
            transition: 'ease 0.3s background-color',
            backgroundColor: isActive
              ? '#ef4444'
              : !cueBackgroundColor || cueBackgroundColor === 'transparent'
                ? '#161616'
                : cueBackgroundColor,
            borderRadius: '0.25rem',
          }
        : {
            transition: 'ease 0.3s background-color',
            backgroundColor:
              !cueBackgroundColor || cueBackgroundColor === 'transparent'
                ? 'transparent'
                : cueBackgroundColor,
            borderRadius: '0.25rem',
          },
    [isTypeCue, isActive, cueBackgroundColor],
  )

  if (!timestampState) return null

  return (
    <div
      className="relative rounded-sm grid gap-4 text-2xl"
      style={buildGridStyle(visibleColumns.length)}
    >
      {isActive && (
        <Suspense>
          <ScrollIntoView
            following
            marginTop={visibleColumns.length > 0 ? '-30px' : '0'}
          />
        </Suspense>
      )}

      <div className="flex items-center gap-8 p-3 w-full" style={cueStyle}>
        {/* Index */}
        <CueIndex>{index}</CueIndex>

        {/* Timing */}
        <CueItemTiming
          timestampId={timestampId}
          timezone={timezone}
          todDisplayFormat={todDisplayFormat}
          isActive={isActive}
        />

        {/* Cue title + subtitle */}
        <div className="min-w-0 grow wrap-break-word">
          <p>{extractTextNewLines(cue.title.trim())}</p>
          <p className="text-sm opacity-80">
            {extractTextNewLines(cue.subtitle.trim())}
          </p>
        </div>
      </div>

      {/* Cue cells */}
      {visibleColumns.map((column) => (
        <CellContent
          key={column.id}
          cell={cells[column.id]}
          col={column}
          style={cueStyle}
        />
      ))}
    </div>
  )
}

function CueItemTiming({
  timestampId,
  timezone,
  todDisplayFormat,
  isActive,
}: Pick<CueItemCueProps, 'timezone' | 'todDisplayFormat'> & {
  timestampId: string
  isActive: boolean
}) {
  const moment = useAtomValue(momentAtom)
  /** Cached atom access that only re-renders when specific state changes  */
  const timestampActualAtom = useMemo(
    () =>
      selectAtom(timestampsAtom, (atoms) => atoms?.cues?.[timestampId]?.actual),
    [timestampId],
  )

  const timestampActual = useAtomValue(timestampActualAtom)

  const { getOption } = useRundownOutputOptions()

  if (!timestampActual) return null

  return (
    <div className="flex flex-col gap-2 w-30 min-w-30">
      {getOption(OUTPUT_TYPES.START_TIMES) !== OUTPUT_TYPES.OFF && (
        <p className="whitespace-nowrap font-mono text-left text-sm opacity-60 leading-3">
          {formatTimeOfDay(timestampActual.start, {
            timezone,
            seconds: 'nonzero',
            format: todDisplayFormat,
          })}
        </p>
      )}
      <div className="font-mono">
        {isActive ? (
          <CueItemElapsed
            moment={moment}
            className="px-0!"
            overtimeClassName="px-1!"
          />
        ) : (
          formatDurationHuman(timestampActual.duration)
        )}
      </div>
      {getOption(OUTPUT_TYPES.END_TIMES) !== OUTPUT_TYPES.OFF && (
        <p className="whitespace-nowrap font-mono text-left text-sm opacity-60 leading-3">
          {formatTimeOfDay(
            addMilliseconds(timestampActual.start, timestampActual.duration),
            {
              timezone,
              seconds: 'nonzero',
              format: todDisplayFormat,
            },
          )}
        </p>
      )}
    </div>
  )
}

function CellContent({
  cell,
  col,
  style,
}: {
  cell: Cell | undefined
  col: Column
  style: CSSProperties
}) {
  if (col?.type === 'select') {
    const selectOptions = col.options || []

    const selectedValue = getContentForSelectCell(cell?.content)

    const selectedValueColor =
      CELL_SELECT_COLORS[_indexOf(selectOptions, selectedValue)]

    return (
      <div className="p-2">
        <CellTypeSelectStatic
          selectedValue={cell?.content?.selected}
          selectedValueColor={selectedValueColor}
        />
      </div>
    )
  }

  const cellContentAsHTML = getContentForTextCell(cell?.content)

  return (
    <div className="rounded-sm overflow-hidden" style={style}>
      <ContentPreviewRenderer contentAsHTML={cellContentAsHTML} />
    </div>
  )
}
